import * as React from "react";

import { useHonoClient } from "~/hooks/HonoContext";
import { supabaseClient } from "~/lib/supabase";
import { storageAdapter } from "../mmkvStorageAdapter";
import { useSyncedStore } from "../syncedStore";
import { apiDataToStoreSnapshot } from "./api";
import { AccountModel, AccountType } from "./store";

type AccountStoreContext =
  | {
      isReady: false;
      store: null;
    }
  | { isReady: true; store: AccountType };

const AccountStoreContext = React.createContext<AccountStoreContext | null>(
  null,
);

export function useAccountStore<
  T extends boolean = false,
  TOut = T extends true
    ? AccountStoreContext
    : { isReady: true; store: AccountType },
>(allowNotReady?: T) {
  const store = React.useContext(AccountStoreContext);
  if (!store)
    throw new Error(
      "useAccountStore must be used within an AccountStoreProvider",
    );
  if (!allowNotReady === true && !store.isReady) {
    throw new Error("useAccountStore was used before the store was ready");
  }
  return store as TOut;
}

export function AccountStoreProvider(props: {
  children: React.ReactNode;
  accountId: string;
}) {
  const { hc } = useHonoClient();

  const storeFetch = React.useCallback(
    async (id: string) => {
      const result = await hc["account-dump"][":accountId"].$get({
        param: { accountId: id },
      });
      if (result.ok) {
        const { data } = await result.json();
        if (data) {
          return apiDataToStoreSnapshot(data);
        }
        throw new Error("No data returned from API");
      }
      throw new Error(`API request failed with status ${result.status}`);
    },
    [hc],
  );

  const getSessionToken = React.useCallback(async () => {
    const session = await supabaseClient.auth.getSession();
    return session?.data.session?.access_token;
  }, []);

  const store = useSyncedStore({
    id: props.accountId,
    storeModel: AccountModel,
    storageAdapter,
    storeFetch,
    getSessionToken,
  });

  return (
    <AccountStoreContext.Provider value={store}>
      {props.children}
    </AccountStoreContext.Provider>
  );
}
